import { useState, useEffect, Fragment } from "react";
import { Typography, Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { findProductById, updateProduct } from "../../../Redux/Customers/Product/Action";
import { useParams, useNavigate } from "react-router-dom";
import { findCategories } from "../../../Redux/Customers/Categories/Action";

const defaultSizes = [
  { name: "S", quantity: 0 },
  { name: "M", quantity: 0 },
  { name: "L", quantity: 0 },
];

const UpdateProductForm = () => {
  const [productData, setProductData] = useState({
    imageUrl: "",
    brand: "",
    title: "",
    colors: [
      { colorName: "", sizes: defaultSizes.map(size => ({ ...size })) }
    ],
    discountedPrice: "",
    price: "",
    discountPersent: "",
    category: "",
    description: "",
  });

  const dispatch = useDispatch();
  const { productId } = useParams();
  const { customersProduct, categories, loading, error } = useSelector((store) => store);
  const navigate = useNavigate();

  // Fetch product data on mount
  useEffect(() => {
    dispatch(findProductById({ productId }));
    dispatch(findCategories()); // Fetch categories on mount
  }, [dispatch, productId]);

  // Populate form when product data is available
  useEffect(() => {
    if (customersProduct.product) {
      setProductData((prevState) => ({
        ...prevState,
        ...customersProduct.product,
        colors: customersProduct.product.colors.map(color => ({
          ...color,
          sizes: color.sizes.map(size => ({ ...size }))
        }))
      }));
    }
  }, [customersProduct.product]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleColorChange = (e, colorIndex) => {
    const { name, value } = e.target;
    const colors = [...productData.colors];
    colors[colorIndex][name] = value;
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleSizeChange = (e, colorIndex, sizeIndex) => {
    let { name, value } = e.target;
    name = name === "size_quantity" ? "quantity" : name;
    const colors = [...productData.colors];
    colors[colorIndex].sizes[sizeIndex][name] = value;
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleAddSize = (colorIndex) => {
    const colors = [...productData.colors];
    colors[colorIndex].sizes.push({ name: "", quantity: 0 });
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleRemoveSize = (colorIndex, sizeIndex) => {
    const colors = [...productData.colors];
    colors[colorIndex].sizes.splice(sizeIndex, 1);
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleAddColor = () => {
    setProductData((prevState) => ({
      ...prevState,
      colors: [...prevState.colors, { colorName: "", sizes: defaultSizes.map(size => ({ ...size })) }],
    }));
  };

  const handleRemoveColor = (colorIndex) => {
    const colors = [...productData.colors];
    colors.splice(colorIndex, 1);
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(updateProduct(productId, productData));
    navigate('/admin/products');
  };

  return (
    <Fragment>
      <Typography variant="h3" sx={{ textAlign: "center" }} className="py-10">
        Update Product
      </Typography>
      {loading && <p>Loading product data...</p>}
      {error && <p>Error loading product data: {error}</p>}
      <form onSubmit={handleSubmit} className="createProductContainer min-h-screen">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image URL"
              name="imageUrl"
              value={productData.imageUrl}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Brand"
              name="brand"
              value={productData.brand}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={productData.title}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            {productData.colors.map((color, colorIndex) => (
              <div className="mt-2" key={colorIndex}>
                <Grid container item spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Color Name"
                      name="colorName"
                      value={color.colorName}
                      onChange={(event) => handleColorChange(event, colorIndex)}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={2} className="flex items-center">
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleRemoveColor(colorIndex)}
                      disabled={productData.colors.length <= 1}
                    >
                      Remove Color
                    </Button>
                  </Grid>
                </Grid>

                {color.sizes.map((size, sizeIndex) => (
                  <Grid container item spacing={5} key={sizeIndex}>
                    <Grid item xs={12} sm={4}>
                      <div className="mt-4">
                        <TextField
                          label="Size Name"
                          name="name"
                          value={size.name}
                          onChange={(event) => handleSizeChange(event, colorIndex, sizeIndex)}
                          required
                          fullWidth
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <div className="mt-4">
                        <TextField
                          label="Quantity"
                          name="size_quantity"
                          type="number"
                          value={size.quantity}
                          onChange={(event) => handleSizeChange(event, colorIndex, sizeIndex)}
                          required
                          fullWidth
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={2} className="flex items-center">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleRemoveSize(colorIndex, sizeIndex)}
                        disabled={color.sizes.length <= 1}
                      >
                        Remove Size
                      </Button>
                    </Grid>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Box mt={2}>
                    <Button variant="outlined" color="primary" onClick={() => handleAddSize(colorIndex)}>
                      Add Size
                    </Button>
                  </Box>
                </Grid>
              </div>
            ))}

            <Grid item xs={12}>
              <Box mt={3}>
                <Button variant="outlined" color="primary" onClick={handleAddColor}>
                  Add Color
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select name="category" value={productData.category} onChange={handleChange} label="Category">
                {categories && categories.categories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="outlined-multiline-static"
              label="Description"
              multiline
              name="description"
              rows={3}
              onChange={handleChange}
              value={productData.description}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Price"
              name="price"
              value={productData.price}
              onChange={handleChange}
              type="number"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Discounted Price"
              name="discountedPrice"
              value={productData.discountedPrice}
              onChange={handleChange}
              type="number"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Discount Percentage"
              name="discountPersent"
              value={productData.discountPersent}
              onChange={handleChange}
              type="number"
            />
          </Grid>
          <Grid item xs={12} className="mt-4">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="w-full"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Product"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Fragment>
  );
};

export default UpdateProductForm;
